import React, { FC, useCallback, useMemo, useState } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Warehouse, getWorkingScheduleString, useOpenWarehouseDoorMutation } from 'entities/Warehouse';
import { ReactComponent as LocationIcon } from 'shared/assets/icons/newIcons/LocationIconBig.svg';
import { ReactComponent as ClockIcon } from 'shared/assets/icons/newIcons/ClockIcon.svg';
import { ReactComponent as WifiIcon } from 'shared/assets/icons/newIcons/WifiIconBig.svg';
import { ReactComponent as ContactIcon } from 'shared/assets/icons/newIcons/ContactIconBig.svg';
import { ReactComponent as CallIcon } from 'shared/assets/icons/PhoneGrayIcon.svg';
import { ReactComponent as SendEmailIcon } from 'shared/assets/icons/SendEmailIcon.svg';
import { ReactComponent as CollapseArrow } from 'shared/assets/icons/CollapseArrow.svg';
import { ReactComponent as VideoIcon } from 'shared/assets/icons/VideoIcon.svg';
import WarehouseDefaultImage from 'shared/assets/images/WarehouseDefaultImage.png';
import { Image } from 'antd';
import { Button } from 'shared/ui/Button';
import { MessagePanel } from 'shared/ui/MessagePanel';
import { Modal } from 'shared/ui/Modal';
import { ShowOnMapButton } from 'features/ShowOnMapButton';
import { CopyTooltip } from 'shared/ui/CopyTooltip';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { showNotification } from 'app/providers/NotificationsProvider';
import { useGeolocation } from 'shared/utils/hooks/useGeolocation';
import { ConfirmModal } from 'shared/ui/ConfirmModal';
import { ReadMore } from 'shared/ui/ReadMore';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';

interface MyBookingWarehouseCardProps {
  warehouse: Nullable<Warehouse>;
  type: 'reservation' | 'contract';
}

export const MyBookingWarehouseCard: FC<MyBookingWarehouseCardProps> = (props) => {
  const { warehouse, type } = props;
  const [isModalOpened, setModalOpened] = useState<boolean>(false);
  const [isOpenedConfirm, setOpenedConfirm] = useState<boolean>(false);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);

  const { t } = useAppTranslation(['contracts', 'common']);

  const coords = useGeolocation();

  const { isDesktop } = useScreenBreakpoints();

  const warehouseImage = warehouse?.imageUrl || WarehouseDefaultImage;

  const [openWarehouseDoor, { isLoading: isOpenLoading }] = useOpenWarehouseDoorMutation();
  const [isCollapseOpen, setIsCollapseOpen] = useState(true);

  const isReservation = type === 'reservation';

  const fullAddress = `${warehouse?.address} ${warehouse?.zip || ''}`;

  const workingScheduleString = getWorkingScheduleString(warehouse?.workingSchedule || [], t);

  const hasMoreThanOneEquipments = warehouse?.internalEquipments && warehouse.internalEquipments.length > 1;

  const handleCloseModal = useCallback((): void => {
    setModalOpened(false);
  }, []);

  const handleOpenModal = useCallback((): void => {
    setModalOpened(true);
  }, []);

  const openConfirmModal = useCallback((): void => {
    setOpenedConfirm(true);
  }, []);

  const closeConfirmModal = useCallback((): void => {
    setOpenedConfirm(false);
  }, []);

  const handleOpenDoor = useCallback(
    async (lockAddress: Nullable<string>): Promise<void> => {
      if (warehouse && lockAddress) {
        try {
          await openWarehouseDoor({
            warehouseId: warehouse.warehouseId,
            args: { lockAddress, longitude: coords?.lng, latitude: coords?.lat },
          }).unwrap();
          showNotification('info', t('Success', { ns: 'common' }), t('The door has been successfully opened', { ns: 'common' }));
        } catch (error: CustomAny) {
          console.log(error);
          const errorMessage = t(error?.data?.message?.[0] || 'Error while opening door', { ns: 'common' });

          showNotification('error', t('Error', { ns: 'common' }), errorMessage);
        }
      }
    },
    [coords?.lat, coords?.lng, openWarehouseDoor, t, warehouse],
  );

  const singleInternalEquipmentLockAddress = useMemo(() => {
    if (warehouse?.internalEquipments && warehouse?.internalEquipments[0]?.lockAddress) {
      return warehouse?.internalEquipments[0]?.lockAddress;
    }
  }, [warehouse?.internalEquipments]);

  const mobileVersion = (
    <div className="bg-white rounded-lg">
      {/* Arrow Row - Always visible at the bottom */}
      {!isCollapseOpen && (
        <div
          className="flex justify-center py-3 cursor-pointer"
          onClick={() => {
            setIsCollapseOpen((prev) => !prev);
          }}
        >
          <CollapseArrow className={`ease-linear duration-200 fill-primaryLight ${isCollapseOpen ? '-rotate-90' : 'rotate-90'}`} />
        </div>
      )}

      {/* Collapsible Content - Entire warehouse info */}
      {isCollapseOpen && (
        <div className="relative">
          {/* Warehouse Image */}
          <div className="w-full h-[250px] rounded-lg overflow-hidden">
            <img src={warehouseImage} alt={warehouse?.name || 'Warehouse'} className="w-full h-full object-cover" />
          </div>

          {/* Info Card - same width as image */}
          <div className="relative -mt-6 bg-white rounded-lg overflow-hidden">
            <div className="flex flex-col p-4 bg-white rounded-xl gap-3">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-xl font-tektur">{warehouse?.name}</span>
                {!warehouse?.workingSchedule?.length && (
                  <div className="text-success border border-success rounded-2xl py-1 px-3 font-tektur">{t('Opened')}</div>
                )}
              </div>

              <ReadMore html={getLocalizedString(warehouse?.description)} rows={2} className="!text-grey !text-sm" />

              <div className="flex justify-between">
                <div>
                  {warehouse?.videoGuideUrl && (
                    <a
                      href={warehouse.videoGuideUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center cursor-pointer text-primaryLight rounded-3xl border border-primaryLight py-2 px-4 font-tektur hover:text-accent"
                    >
                      <VideoIcon className="mr-2 text-primaryLight" />
                      {t('Video guide')}
                    </a>
                  )}
                </div>
                <div className="flex gap-2">
                  <a href={`tel:${warehouse?.phone}`} className="flex items-center p-2 border rounded-lg border-grey">
                    <CallIcon />
                  </a>
                  <a href={`mailto:${warehouse?.email}`} className="flex items-center p-2 border rounded-lg border-grey">
                    <SendEmailIcon />
                  </a>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex items-center text-primary gap-4 !text-grey">
                  <LocationIcon />
                  <CopyTooltip className="text-sm text-primaryLight">{fullAddress}</CopyTooltip>
                </div>

                <div className="flex items-center text-primaryLight text-sm gap-4">
                  <ClockIcon />
                  {workingScheduleString}
                </div>
              </div>

              {!isReservation && (
                <div className="flex items-center text-primary gap-4">
                  <WifiIcon />
                  <div className="flex flex-col">
                    <span className="text-sm text-primaryLight">{warehouse?.wifiNetworkName}</span>
                    <span className="text-sm text-primaryLight">{warehouse?.wifiPassword}</span>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-2 mt-3">
                {warehouse?.latitude && warehouse?.longitude && (
                  <ShowOnMapButton latitude={warehouse.latitude} longitude={warehouse.longitude} theme="dark" containerClassName="w-full" />
                )}
              </div>
            </div>
          </div>
          <div
            className="flex justify-center pb-2 cursor-pointer"
            onClick={() => {
              setIsCollapseOpen((prev) => !prev);
            }}
          >
            <CollapseArrow className={`ease-linear duration-200 fill-primaryLight ${isCollapseOpen ? '-rotate-90' : 'rotate-90'}`} />
          </div>
        </div>
      )}
    </div>
  );

  const desktopVersion = (
    <div className="flex flex-col justify-between p-6 bg-white rounded-xl">
      <div className="flex gap-1.5">
        <div className="flex flex-col flex-1 gap-2 pr-2">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-3xl font-tektur">{warehouse?.name}</span>
            {!warehouse?.workingSchedule?.length && (
              <div className="text-success rounded-3xl border border-success py-2 px-5 font-tektur">{t('Opened')}</div>
            )}
          </div>

          <ReadMore html={getLocalizedString(warehouse?.description)} rows={3} className="!text-grey" />

          <div className="flex items-center justify-between">
            <div className="flex items-center text-grey">
              <LocationIcon className="mr-4" />
              <CopyTooltip className="text-primaryLight text-sm">{fullAddress}</CopyTooltip>
            </div>
            {warehouse?.videoGuideUrl && (
              <a
                href={warehouse.videoGuideUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center cursor-pointer text-primaryLight rounded-3xl border border-primaryLight py-2 px-4 font-tektur hover:text-accent"
              >
                <VideoIcon className="mr-2" />
                {t('Video guide')}
              </a>
            )}
          </div>

          <div className="flex items-center">
            <ClockIcon className="mr-4" />
            <div className="text-primary text-sm text-primaryLight">{workingScheduleString}</div>
          </div>

          <div className="flex items-center">
            <ContactIcon className="mr-4" />
            <div className="flex flex-col">
              <a href={`tel:${warehouse?.phone}`} className="text-primary text-sm text-primaryLight">
                {warehouse?.phone}
              </a>
              <a href={`mailto:${warehouse?.email}`} className="text-primary text-sm text-primaryLight">
                {warehouse?.email}
              </a>
            </div>
          </div>

          {!isReservation && (
            <div className="flex items-center text-primary gap-4">
              <WifiIcon />
              <div className="flex flex-col">
                <span className="text-sm text-primaryLight">{warehouse?.wifiNetworkName}</span>
                <span className="text-sm text-primaryLight">{warehouse?.wifiPassword}</span>
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 flex justify-center">
          <Image
            width="100%"
            className="rounded-md object-cover max-h-[350px]"
            src={warehouse?.imageUrl || ''}
            preview={{
              visible: isPreviewVisible,
              scaleStep: 3,
              src: warehouse?.imageUrl || '',
              onVisibleChange: setIsPreviewVisible,
            }}
          />
        </div>
      </div>

      <div className="flex gap-1.5 mt-4">
        {!isReservation && (
          <>
            {hasMoreThanOneEquipments ? (
              <Button iconPosition="prev" onClick={handleOpenModal} containerClassName="w-full">
                {t('Doors or gates')}
              </Button>
            ) : (
              <Button iconPosition="prev" onClick={openConfirmModal} containerClassName="w-full">
                {t('Open')}
              </Button>
            )}
          </>
        )}
        {warehouse?.latitude && warehouse?.longitude && (
          <ShowOnMapButton latitude={warehouse.latitude} longitude={warehouse.longitude} theme="dark" containerClassName="w-full" />
        )}
      </div>
    </div>
  );

  const content = isDesktop ? desktopVersion : mobileVersion;

  return (
    <>
      {content}

      <ConfirmModal
        isOpened={isOpenedConfirm}
        title={t('Open door', { ns: 'common' })}
        description={t('Are you sure you want to open door', { ns: 'common' })}
        isLoading={isOpenLoading}
        onOk={() => {
          if (singleInternalEquipmentLockAddress) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            handleOpenDoor(singleInternalEquipmentLockAddress);
          }
        }}
        onCancel={closeConfirmModal}
      />

      <Modal isOpen={isModalOpened} onClose={handleCloseModal} closeIcon={false} paddingLess width={360} footer={false}>
        <MessagePanel
          className="w-[360px]"
          type="info"
          title={t('Choose the door you need')}
          description={t('Click the button to access')}
          actions={
            <div className="flex flex-col justify-center gap-3">
              {hasMoreThanOneEquipments &&
                warehouse.internalEquipments?.map((equipment) => (
                  <Button
                    isLoading={isOpenLoading}
                    key={equipment.internalEquipmentId}
                    onClick={() => {
                      // eslint-disable-next-line @typescript-eslint/no-floating-promises
                      handleOpenDoor(equipment.lockAddress);
                    }}
                  >
                    {getLocalizedString(equipment.name)}
                  </Button>
                ))}

              <Button theme="outlined" onClick={handleCloseModal} isLoading={isOpenLoading} className="mt-4">
                {t('Cancel')}
              </Button>
            </div>
          }
        />
      </Modal>
    </>
  );
};
